/** 학습계획으로 프레젠테이션·활동지 문안을 만든다. 키는 서버에만 둔다. */
import { supabase } from '../supabaseClient';
import { aiAccessErrorMessage, syncAiQuotaFromResponse } from '../services/aiQuotaService';

export type AiLearningSlideDraft = {
  kind: 'title' | 'stage';
  stage: string;
  title: string;
  body: string;
  question: string;
};

export type AiLearningWorksheetTaskDraft = {
  prompt: string;
  writeLines: number;
};

export type AiLearningWorksheetPageDraft = {
  title: string;
  lead: string;
  tasks: AiLearningWorksheetTaskDraft[];
};

/** 활동지 v2 블록 (서버 schemaVersion 2, 모든 필드가 항상 있음) */
export const AI_WORKSHEET_BLOCK_TYPES = [
  'reading',
  'short',
  'long',
  'cloze',
  'choice',
  'ox',
  'matching',
  'ordering',
  'classify',
  'tableFill',
  'organizer',
  'checklist',
  'selfAssess',
  'drawing',
  'html',
] as const;
export type AiWorksheetBlockType = (typeof AI_WORKSHEET_BLOCK_TYPES)[number];
export type AiWorksheetStage = '도입' | '배움1' | '배움2' | '정리';

export type AiWorksheetBlock = {
  id: string;
  type: AiWorksheetBlockType;
  variant: '' | 'kwl' | 'tchart' | 'blank' | 'grid' | 'dots';
  stage: AiWorksheetStage;
  title: string;
  size: 'S' | 'M' | 'L';
  /** 예전 2단 구성의 흔적. 지금은 항상 2(가로 전체)로 정규화한다 */
  span: 1 | 2;
  optional: boolean;
  text: string;
  items: string[];
  pairs: Array<{ left: string; right: string }>;
  categories: string[];
  rows: string[];
  lines: number;
  html: string;
  wordBank: string[];
  /** 정리 단계 형성평가 문제(choice·ox·short·cloze). 예전 응답에는 없을 수 있음 → false */
  formative: boolean;
};

/** 단계 이름표 오른쪽 소제목(단계별, 없으면 앱 기본 문구) */
export type AiWorksheetStageTitles = Partial<Record<AiWorksheetStage, string>>;

export type AiWorksheetV2 = {
  schemaVersion: 2;
  /** pages: AI가 잡은 쪽 수(되도록 2, 많아도 3). 배치기는 실제 높이로 1~3쪽을 다시 정한다 */
  design: { pages: 1 | 2 | 3; flow: string; stageTitles: AiWorksheetStageTitles };
  title: string;
  blocks: AiWorksheetBlock[];
};

export type AiLearningMaterialContext = {
  kind: 'presentation' | 'worksheet';
  plan: string;
  subject: string;
  grade: string;
  unit: string;
  learning: string;
  achievement: string;
  /** 다시 만들기: 지난번에 쓴 블록 유형(되도록 피함) */
  avoidTypes?: string[];
};

const ERRORS: Record<string, string> = {
  AI_NOT_CONFIGURED: 'AI 키가 설정되지 않아 만들지 못했습니다.',
  UNAUTHORIZED: '로그인이 필요합니다. 다시 로그인해 주세요.',
  RATE_LIMITED: '요청이 많습니다. 잠시 후 다시 시도해 주세요.',
  EMPTY_PLAN: '학습계획이 있어야 만들 수 있습니다.',
  EMPTY_RESULT: '만든 내용이 비어 있습니다. 한 번 더 시도해 주세요.',
  INVALID_KIND: '만들 자료 종류가 올바르지 않습니다.',
};

function apiUrl(): string {
  const base = (process.env.REACT_APP_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';
  return `${base}/api/ai-learning-material`;
}

export async function requestAiLearningMaterial(
  input: AiLearningMaterialContext
): Promise<
  | { kind: 'presentation'; slides: AiLearningSlideDraft[] }
  | { kind: 'worksheet'; worksheet: AiWorksheetV2 }
  | { kind: 'worksheet'; pages: AiLearningWorksheetPageDraft[] }
> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error(ERRORS.UNAUTHORIZED);
  let res: Response;
  try {
    res = await fetch(apiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(input),
    });
  } catch {
    throw new Error('AI 서버에 연결하지 못했습니다.');
  }
  const json = (await res.json().catch(() => null)) as {
    ok?: boolean;
    error?: string;
    kind?: string;
    slides?: unknown;
    pages?: unknown;
    schemaVersion?: unknown;
    worksheet?: unknown;
  } | null;
  syncAiQuotaFromResponse(json);
  if (!json || !json.ok) {
    if (!json && res.status === 404) {
      throw new Error('AI 요청이 서버에 연결되지 않았습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.');
    }
    const code = json?.error || `HTTP_${res.status}`;
    throw new Error(aiAccessErrorMessage(code, json) ?? ERRORS[code] ?? '자료를 만들지 못했습니다. 다시 시도해 주세요.');
  }
  if (json.kind === 'presentation' && Array.isArray(json.slides) && json.slides.length) {
    return { kind: 'presentation', slides: json.slides as AiLearningSlideDraft[] };
  }
  if (json.kind === 'worksheet' && json.schemaVersion === 2) {
    const worksheet = coerceAiWorksheetV2(json.worksheet);
    if (worksheet) return { kind: 'worksheet', worksheet };
  }
  if (json.kind === 'worksheet' && Array.isArray(json.pages) && json.pages.length) {
    return { kind: 'worksheet', pages: json.pages as AiLearningWorksheetPageDraft[] };
  }
  throw new Error(ERRORS.EMPTY_RESULT);
}

function strList(raw: unknown, max: number): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim())
    .filter(Boolean)
    .slice(0, max);
}

/** 서버가 이미 정리하지만, 저장된 aiSource 등 외부 JSON을 안전하게 읽기 위해 한 번 더 강제 */
export function coerceAiWorksheetBlock(raw: unknown): AiWorksheetBlock | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const type = String(o.type ?? '') as AiWorksheetBlockType;
  if (!(AI_WORKSHEET_BLOCK_TYPES as readonly string[]).includes(type)) return null;
  const stages: AiWorksheetStage[] = ['도입', '배움1', '배움2', '정리'];
  const stage = stages.includes(o.stage as AiWorksheetStage) ? (o.stage as AiWorksheetStage) : '배움1';
  const variants = ['', 'kwl', 'tchart', 'blank', 'grid', 'dots'] as const;
  const variant = (variants as readonly string[]).includes(String(o.variant ?? ''))
    ? (String(o.variant ?? '') as AiWorksheetBlock['variant'])
    : '';
  const pairs = Array.isArray(o.pairs)
    ? (o.pairs as unknown[])
        .map((p) => {
          const r = (p ?? {}) as Record<string, unknown>;
          return { left: String(r.left ?? '').trim(), right: String(r.right ?? '').trim() };
        })
        .filter((p) => p.left && p.right)
        .slice(0, 6)
    : [];
  const lines = Number(o.lines);
  return {
    id: typeof o.id === 'string' && o.id.trim() ? o.id.trim().slice(0, 24) : '',
    type,
    variant,
    stage,
    title: String(o.title ?? '').trim().slice(0, 120),
    size: o.size === 'S' || o.size === 'L' ? o.size : 'M',
    // 1단 구성: 예전 데이터·AI 답의 span=1도 가로 전체로 맞춘다
    span: 2,
    optional: o.optional === true,
    text: String(o.text ?? '').slice(0, 1200),
    items: strList(o.items, 12),
    pairs,
    categories: strList(o.categories, 6),
    rows: strList(o.rows, 8),
    lines: Number.isFinite(lines) ? Math.max(0, Math.min(10, Math.round(lines))) : 0,
    html: typeof o.html === 'string' ? o.html.slice(0, 8000) : '',
    wordBank: strList(o.wordBank, 12),
    formative: o.formative === true && ['choice', 'ox', 'short', 'cloze'].includes(type) && stage === '정리',
  };
}

/** design.stageTitles: 객체({도입: '…'}) 또는 배열([{stage, title}]) 모두 읽는다 */
export function coerceAiWorksheetStageTitles(raw: unknown): AiWorksheetStageTitles {
  const out: AiWorksheetStageTitles = {};
  const stages: AiWorksheetStage[] = ['도입', '배움1', '배움2', '정리'];
  const rows: Array<{ stage: unknown; title: unknown }> = Array.isArray(raw)
    ? (raw as unknown[]).map((r) => (r ?? {}) as { stage: unknown; title: unknown })
    : raw && typeof raw === 'object'
      ? Object.entries(raw as Record<string, unknown>).map(([stage, title]) => ({ stage, title }))
      : [];
  for (const row of rows) {
    const stage = String(row.stage ?? '').replace(/\s+/g, '') as AiWorksheetStage;
    if (!stages.includes(stage) || out[stage]) continue;
    const title = String(row.title ?? '').replace(/\s+/g, ' ').trim().slice(0, 30);
    if (title) out[stage] = title;
  }
  return out;
}

export function coerceAiWorksheetV2(raw: unknown): AiWorksheetV2 | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const blocks = (Array.isArray(o.blocks) ? o.blocks : [])
    .map(coerceAiWorksheetBlock)
    .filter((b): b is AiWorksheetBlock => b != null)
    .slice(0, 12);
  if (!blocks.length) return null;
  blocks.forEach((b, i) => {
    if (!b.id) b.id = `b${i + 1}`;
  });
  const design = (o.design ?? {}) as Record<string, unknown>;
  return {
    schemaVersion: 2,
    design: {
      // 최대 2쪽: 예전 데이터의 3쪽 설계도 2쪽으로 읽는다
      pages: Number(design.pages) >= 2 ? 2 : 1,
      flow: String(design.flow ?? '').slice(0, 200),
      stageTitles: coerceAiWorksheetStageTitles(design.stageTitles),
    },
    title: String(o.title ?? '').trim().slice(0, 60),
    blocks,
  };
}
